<template>
  <!-- Header -->
  <div class="relative bg-primary md:pt-32 pb-32 pt-12">
    <div
      class="px-4 md:px-10 mx-auto w-full"
      v-if="displayFilterStats || displayHeaderStats || displayCostoPersonal"
    >
      <div>
        <!-- Filter stats -->
        <filter-stats v-if="displayFilterStats" />

        <!-- Card stats - Costo Personal Activo -->
        <div class="flex flex-wrap" v-if="displayCostoPersonal">
          <div class="w-full px-4 pb-4">
            <h2
              class="text-white text-sm uppercase hidden lg:inline-block font-semibold"
            >
              {{ $t("Costo Personal Activo (USD/hora)") }}
            </h2>
          </div>
          <div
            class="w-full lg:w-6/12 xl:w-3/12 px-4 pb-4"
            v-for="(personal, index) in costoPersonalActivo"
            :key="personal.idpersonal"
          >
            <card-stats
              :statSubtitle="personal.nombre"
              :statTitle="removeTrailingZeros(personal.costo_hora_comparativo)"
              statPercentColor="text-emerald-500"
              statIconName="fas fa-dollar-sign"
              :statIconColor="getColor(index)"
            />
          </div>
          <div
            v-if="!costoPersonalActivo?.length"
            class="w-full lg:w-6/12 xl:w-3/12 px-4 pb-4"
          >
            <card-stats
              :statSubtitle="$t('No hay personal activo con costos cargados')"
              statIconName="fa fa-xmark"
              statIconColor="bg-blueGray-500"
            ></card-stats>
          </div>
        </div>

        <!-- Card stats - Horas por tarea -->
        <div class="flex flex-wrap" v-if="displayHeaderStats">
          <div class="w-full px-4 pb-4">
            <h2
              class="text-white text-sm uppercase hidden lg:inline-block font-semibold"
            >
              {{ $t("Horas por tarea") }}
            </h2>
          </div>
          <div
            class="w-full lg:w-6/12 xl:w-3/12 px-4 pb-4"
            v-for="(tarea, index) in horasPorTarea"
            :key="tarea.idtarea"
          >
            <card-stats
              :statSubtitle="tarea.nombre"
              :statTitle="removeTrailingZeros(tarea.horas)"
              statPercentColor="text-emerald-500"
              statIconName="far fa-clock"
              :statIconColor="getColor(index)"
            />
          </div>
          <div
            v-if="!horasPorTarea?.length"
            class="w-full lg:w-6/12 xl:w-3/12 px-4 pb-4"
          >
            <card-stats
              :statSubtitle="
                $t('No se han cargado horas para el filtro seleccionado')
              "
              statIconName="fa fa-xmark"
              statIconColor="bg-blueGray-500"
            ></card-stats>
          </div>
          <div v-else class="w-full lg:w-6/12 xl:w-3/12 px-4 pb-4">
            <card-stats
              statSubtitle="Total"
              :statTitle="totalHoras"
            ></card-stats>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CardStats from "@/components/Cards/CardStats.vue";
import FilterStats from "@/components/Forms/FilterStats.vue";
import ReporteService from "@/services/reporte.service";
import NumberUtils from "@/utils/numberUtils";

export default {
  components: {
    CardStats,
    FilterStats,
  },

  data() {
    return {
      horasPorTarea: [],
      costoPersonalActivo: [],
    };
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
      startDateFromState: (state) => state.filters.startDate,
      endDateFromState: (state) => state.filters.endDate,
    }),
    currentRoutePath() {
      return this.$route.path;
    },
    isAdmin() {
      return this.userInfo?.roles?.includes("admin");
    },
    displayHeaderStats() {
      return ["/", "/horas"].includes(this.currentRoutePath);
    },
    displayCostoPersonal() {
      return this.isAdmin && this.currentRoutePath === "/personal";
    },
    displayFilterStats() {
      if ("/horas" === this.currentRoutePath) {
        return true;
      }
      return (
        this.isAdmin && ["/", "/transacciones"].includes(this.currentRoutePath)
      );
    },
    totalHoras() {
      const totalHoras = this.horasPorTarea?.reduce((total, item) => {
        return total + parseFloat(item.horas);
      }, 0);
      return `${totalHoras}`;
    },
  },

  mounted() {
    this.loadRecords();
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        ["startDateFromState", "endDateFromState"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              this.loadRecords();
            },
          );
        });
      },
    },
  },

  methods: {
    getColor(i) {
      let colors = [
        "bg-red-500",
        "bg-emerald-500",
        "bg-purple-500",
        "bg-lightBlue-500",
        "bg-orange-500",
      ];
      return colors[i % colors.length];
    },

    loadRecords() {
      // Se pide sólo donde el widget se muestra: en otras pantallas el filtro
      // de la ruta pertenece a otra entidad y no le corresponde (en Inicio,
      // por ejemplo, filtra por proyecto). Va antes de la guarda de fechas
      // porque el costo por hora no depende del rango de tiempo, y en Personal
      // ese selector ni siquiera existe.
      if (this.displayCostoPersonal) {
        // Se le pasa el filtro del listado de Personal, para que el widget
        // no muestre gente que la tabla está ocultando
        ReporteService.costoPersonalActivo({
          filters: this.$route.query.filters,
        })
          .then((data) => {
            this.costoPersonalActivo = data.records;
          })
          .catch((response) => {
            console.log(response);
          });
      }

      // undefined = FilterStats aún no inicializó; null = histórico (sin filtro de fechas)
      if (
        this.startDateFromState === undefined ||
        this.endDateFromState === undefined
      ) {
        return;
      }

      const startDate = this.startDateFromState;
      const endDate = this.endDateFromState;
      // Se le pasa el mismo filtro del listado que acompaña, para que los
      // totales por tarea no incluyan lo que la tabla está ocultando
      const filters = {
        startDate,
        endDate,
        filters: this.$route.query.filters,
      };

      ReporteService.horasPorTarea(filters)
        .then((data) => {
          this.horasPorTarea = data.records;
        })
        .catch((response) => {
          console.log(response);
        });
    },

    removeTrailingZeros(params) {
      return NumberUtils.removeTrailingZeros(params);
    },
  },
};
</script>
